import React from 'react';
import {blue, grey} from '@mui/material/colors';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {ErrorBoundary} from 'react-error-boundary';
import {BrowserRouter, Switch} from 'react-router-dom';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import {AuthProvider, CallbackPage} from '../../auth';
import ErrorFallback from '../../components/ErrorFallback';
import Route from '../../lib/AuthRoute';
import ScrollToTopRoute from '../../lib/ScrollToTopRoute';
import AdminPanel from '../AdminPanel';
import AllSMSHistory from '../AllSMSHistory';
import BlockedContacts from '../BlockedContacts';
import CreateGroup from '../CreateGroup';
import CustomizeSms from '../CustomizeSms';
import DevelopersTool from '../DevelopersTool';
import ManageOperators from '../ManageOperators';
import RateLimits from '../RateLimits';
import MyAccount from '../MyAccount';
import Group from '../Group';
import Guide from '../Guide';
import Login from '../Login';
import NewDashboard from '../NewDashboard';
import NotFound from '../NotFound';
// OpsDashboard is now reached via the Ops view tab inside NewDashboard;
// /ops-dashboard is kept as a route alias that pre-selects that tab so
// existing bookmarks / Slack links continue to work.
// import ScheduleHistory from '../ScheduleHistory';
import Sms from '../Sms';
import SmsHistory from '../SmsHistory';
import Student from '../Student';
import Template from '../Template';
import Website from '../WebSite';
import AboutUs from '../WebSite/aboutUs';
import Api from '../WebSite/api';
import ContactManagementSystem from '../WebSite/contactManagementSystem';
import ContactUs from '../WebSite/contactUs';
import Features from '../WebSite/features';
import OTPSMS from '../WebSite/otpSMS';
import Pricing from '../WebSite/pricing';
import PrivacyPolicy from '../WebSite/privacyPolicy';
import PromotionalSMS from '../WebSite/promotionalSMS';
import Services from '../WebSite/services';
import TermsConditions from '../WebSite/termsConditions';
import TransactionalSMS from '../WebSite/transactionalSMS';
import CreditHistory from '../CreditHistory';
import BalanceNotifications from '../BalanceNotifications';
import EmailTemplates from '../EmailTemplates';


const muiTheme = createTheme({
  palette: {
    mode: 'light',
    default: {
      main: grey[300],
      dark: grey[400]
    },
    primary: {
      main: blue[700]
    },
    secondary: {
      main: blue[600]
    }
  },
  typography: {
    button: {
      textTransform: 'none'
    }
  }
}
);
const publicPath = '/';
window.isMobile = function() {
  var check = false;
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
  return check;
};
export const routeCodes = {
  LOGIN : `${ publicPath }login`,
  SMS : `${ publicPath }sms`,
  CONTACTS : `${ publicPath }contacts`,
  BLOCKED_CONTACTS: `${publicPath}blocked-contacts`,
  SMS_HISTORY : `${ publicPath }sms-reports`,
  CREATE_GROUP : `${ publicPath }create-group`,
  GROUP : `${ publicPath }group/:groupId`,
  GUIDE : `${ publicPath }guide`,
  ADMIN_PANEL : `${ publicPath }admin-panel`,
  BALANCE_NOTIFICATIONS : `${ publicPath }balance-notifications`,
  EMAIL_TEMPLATES : `${ publicPath }email-templates`,
  // The customer landing page after login.
  DASHBOARD : `${ publicPath }dashboard`,
  SCHEDULED_SMS : `${ publicPath }scheduled-sms`,
  CUSTOMIZE_SMS: `${ publicPath }customize-sms`,
  ALL_SMS_HISTORY: `${publicPath}sent-sms`,
  DEVELOPERS_TOOL: `${publicPath}developers-tool`,
  TEMPLATE: `${ publicPath }template`,
  CREDIT_HISTORY: `${ publicPath }credit-history`,
  MANAGE_OPERATORS: `${publicPath}manage-operators`,
  RATE_LIMITS: `${publicPath}rate-limits`,
  OPS_DASHBOARD: `${publicPath}ops-dashboard`,
  MY_ACCOUNT: `${publicPath}my-account`
};
// Route alias for /ops-dashboard — mounts the regular Dashboard but
// asks it to pre-select the Ops view tab. AuthRoute passes `component`
// to react-router, which doesn't forward extra Route props to the
// component, so we wrap NewDashboard here instead of passing a prop on
// the Route element.
const OpsDashboardEntry = () => <NewDashboard defaultTab="ops" />;

const App = () => {
  //TODO: pending icon click issue,remove recharts,  upgrade version of react-redux(later), redux, redux-thunk, react-router-dom
  const IS_SMS_LOGIN_SITE_ONLY = true
  return (
    <AuthProvider>
      <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
        <ThemeProvider theme={muiTheme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <div className='App'>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <Switch>
                  <Route exact path='/callback' component={ CallbackPage } />
                  <Route exact path={ routeCodes.LOGIN } component={ Login } />
                  <Route exact path={ routeCodes.SMS } component={ Sms } authRequired={true} isMenuDrawer={true} title={'Send SMS'} type={'SMS'}/>
                  <Route exact path={ routeCodes.CUSTOMIZE_SMS } component={ CustomizeSms } authRequired={true} isMenuDrawer={true} title={'Customize SMS'} type={'CUSTOMIZE_SMS'}/>
                  <Route exact path={ routeCodes.TEMPLATE } component={ Template } authRequired={true} isMenuDrawer={true} title={'Templates/Headers'} type={'TEMPLATE'}/>
                  <Route exact path={ routeCodes.CONTACTS } component={ Student } authRequired={true} isMenuDrawer={true} title={'Manage Contacts'} type={'CONTACTS'}/>
                  <Route exact path={routeCodes.BLOCKED_CONTACTS} component={BlockedContacts} authRequired isMenuDrawer title="Manage Blocked Numbers" type="BLOCKED_CONTACTS" />
                  <Route exact path={ routeCodes.CREATE_GROUP } component={ CreateGroup } authRequired={true} isMenuDrawer={true} title={'Create Group'} type={'CREATE_GROUP'}/>
                  <Route exact path={ routeCodes.GROUP } component={ Group } authRequired={true} isMenuDrawer={true} title={'Group'} type={'GROUP'}/>
                  <Route exact path={ routeCodes.SMS_HISTORY } component={ SmsHistory } authRequired={true} isMenuDrawer={true} title={'SMS Reports'} type={'SMS_HISTORY'}/>
                  <Route exact path={ routeCodes.GUIDE } component={ Guide } authRequired={true} isMenuDrawer={false} title={''} type={'GUIDE'} isMenuBarOnly={true} />
                  <Route exact path={routeCodes.ADMIN_PANEL} component={AdminPanel} authRequired isMenuDrawer={true} title={'Admin Panel'} type={'ADMIN_PANEL'} isAdmin={true} />
                  <Route exact path={ routeCodes.DASHBOARD } component={ NewDashboard } authRequired={true} isMenuDrawer={true} title={'Dashboard'} type={'DASHBOARD'} />
                  {/* <Route exact path={routeCodes.SCHEDULED_SMS} component={ScheduleHistory} authRequired isMenuDrawer={true} title={'Scheduled SMS'} type={'SCHEDULED_SMS'} /> */}
                  <Route exact path={routeCodes.ALL_SMS_HISTORY} component={AllSMSHistory} authRequired={true} isMenuDrawer={true} title={'Sent Messages'} type={'ALL_SMS_HISTORY'}/>
                  <Route exact path={routeCodes.CREDIT_HISTORY} component={CreditHistory} authRequired={true} isMenuDrawer={true} title={'Credit History'} type={'CREDIT_HISTORY'}/>
                  <Route exact path={routeCodes.DEVELOPERS_TOOL} component={DevelopersTool} authRequired isMenuDrawer={true} title={'Developers Tool'} type={'DEVELOPERS_TOOL'} />
                  <Route exact path={routeCodes.MANAGE_OPERATORS} component={ManageOperators} authRequired isMenuDrawer={true} title={'Manage Operators'} type={'MANAGE_OPERATORS'} isAdmin={true} />
                  <Route exact path={routeCodes.RATE_LIMITS} component={RateLimits} authRequired isMenuDrawer={true} title={'Rate Limits'} type={'RATE_LIMITS'} isAdmin={true} />
                  <Route exact path={routeCodes.BALANCE_NOTIFICATIONS} component={BalanceNotifications} authRequired isMenuDrawer={true} title={'Balance Notifications'} type={'BALANCE_NOTIFICATIONS'} isAdmin={true} />
                  <Route exact path={routeCodes.EMAIL_TEMPLATES} component={EmailTemplates} authRequired isMenuDrawer={true} title={'Email Templates'} type={'EMAIL_TEMPLATES'} isAdmin={true} />
                  <Route
                    exact
                    path={routeCodes.OPS_DASHBOARD}
                    component={OpsDashboardEntry}
                    authRequired
                    isMenuDrawer={true}
                    title={'Ops Dashboard'}
                    type={'DASHBOARD'}
                    isAdmin={true}
                  />
                  <Route exact path={routeCodes.MY_ACCOUNT} component={MyAccount} authRequired isMenuDrawer={true} title={'My Account'} type={'MY_ACCOUNT'} />
                  <ScrollToTopRoute exact path='/' component={ IS_SMS_LOGIN_SITE_ONLY || process.env.REACT_APP_IS_RESELLER === 'true' ? Login : Website } />
                  {!IS_SMS_LOGIN_SITE_ONLY && process.env.REACT_APP_IS_RESELLER !== 'true' ? 
                    <>
                      <ScrollToTopRoute exact path='/bulk-sms-pricing' component={ Pricing } />
                      <ScrollToTopRoute exact path='/bulk-sms-api' component={ Api } />
                      <ScrollToTopRoute exact path='/bulk-sms-services' component={ Services } />
                      <ScrollToTopRoute exact path='/bulk-sms-features' component={ Features } />
                      <ScrollToTopRoute exact path='/contact-us' component={ ContactUs } />
                      <ScrollToTopRoute exact path='/about-us' component={ AboutUs } />
                      <ScrollToTopRoute exact path='/marketing' component={ Website } />
                      <ScrollToTopRoute exact path='/transactional-bulk-sms' component={ TransactionalSMS } />
                      <ScrollToTopRoute exact path='/promotional-bulk-sms' component={ PromotionalSMS } />
                      <ScrollToTopRoute exact path='/otp-sms-services' component={ OTPSMS } />
                      <ScrollToTopRoute exact path='/contact-management-system' component={ ContactManagementSystem } />
                      <ScrollToTopRoute exact path='/terms-and-conditions' component={ TermsConditions } />
                      <ScrollToTopRoute exact path='/privacy-policy' component={ PrivacyPolicy } />
                    </> : null
                  }
                  <Route path='*' component={ NotFound } />
                </Switch>
              </ErrorBoundary>

            
            </div>
          </LocalizationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AuthProvider>
  )
}

export default App
